import SearchIcon from "@mui/icons-material/Search";
import {
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { Dialog } from "components/Dialog";
import { useDebounce } from "hooks/useDebounce";
import { useState } from "react";
import { TextInput } from "routes/Customer/Tasks/TextInput";

const SEARCH_DEBOUNCE_MS = 400;

type ThreadListItem = {
  id: string;
  title: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
};

export function SearchThreadsDialog({ open, onClose }: Props) {
  const [searchInput, setSearchInput] = useState("");
  const trimmed = searchInput.trim();
  const [debouncedSearch, isSearchDebouncing] = useDebounce(trimmed, SEARCH_DEBOUNCE_MS);

  const recents = useRecentThreads();
  const searchResults = useSearchResults(debouncedSearch);

  const showRecents = trimmed.length === 0;
  const showSearchLoading = trimmed.length > 0 && isSearchDebouncing;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      noPrimaryAction
      DialogContentProps={{ sx: { paddingBottom: 2 } }}
      title={
        <TextInput
          placeholder="Search threads..."
          fullWidth
          autoFocus
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          startAdornment={
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          }
        />
      }
    >
      {showRecents ? (
        <ThreadListSection heading="Recents" items={recents} loading={false} />
      ) : (
        <ThreadListSection
          heading="Search results"
          items={searchResults}
          loading={showSearchLoading}
        />
      )}
    </Dialog>
  );
}

function ThreadListSection({
  heading,
  items,
  loading,
}: {
  heading: string;
  items: ThreadListItem[];
  loading?: boolean;
}) {
  const theme = useTheme();
  return (
    <Stack spacing={1} paddingBottom={theme.spacing(1)} sx={{ maxHeight: 200, overflow: "auto" }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="caption">{heading}</Typography>
      </Stack>
      <List disablePadding>
        {loading ? (
          Array.from({ length: 3 }, (_, i) => (
            <ListItemButton key={i}>
              <Skeleton variant="text" width="100%" height={32} />
            </ListItemButton>
          ))
        ) : (
          <>
            {items.map((item) => (
              <ListItemButton
                key={item.id}
                sx={{
                  width: "100%",
                  borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                  "&:last-child": {
                    borderBottom: "none",
                  },
                }}
              >
                <ListItemText primary={item.title} />
              </ListItemButton>
            ))}
          </>
        )}
      </List>
    </Stack>
  );
}

const useRecentThreads = (): ThreadListItem[] => {
  return Array.from({ length: 3 }, (_, i) => ({
    id: (i + 1).toString(),
    title: `Thread ${i + 1}`,
  }));
};

const useSearchResults = (search: string): ThreadListItem[] => {
  if (search.length === 0) {
    return [];
  }
  return Array.from({ length: 10 }, (_, i) => ({
    id: (i + 1).toString(),
    title: `Search result ${i + 1} for ${search}`,
  }));
};
